<template>
  <!--  <FileManager></FileManager>-->
  <div class="flex flex-col md:grid grid-cols-5 bg-white" v-if="$can('get', '/settings/settings')">
    <div class="col-span-1 md:h-screen py-4 px-2 shadow-md relative">
      <template v-for="(section, i) in settings" :key="i">
        <div v-if="!section.isPrivate || useSettingsStore().showPrivateSettings" @click="changeSection(section)"
             class="cursor-pointer flex items-center gap-2 my-2 p-2 bg-[#1D4C751A] hover:bg-[#1D4C75] text-[#1D4C75] hover:text-white"
             :class="{'!bg-[#1D4C75] !text-white': selectedSection?.id == section.id}">
          <Icon v-if="section.icon" :icon="section.icon" class="text-2xl"/>
          <span>{{ $ctx.locale == 'en' ? section.enName : section.arName }}</span>
        </div>
      </template>
      <div class="absolute bottom-[50px] left-0 right-0 p-2">
        <el-button type="primary" @click="applySettingsToAll" class="w-full">{{ t('applySettingsToAll') }}</el-button>
      </div>
      <div v-if="useSettingsStore().showPrivateSettings">
          <BackupUpload />
        </div>
    </div>
    <div v-if="selectedSection" class="col-span-4 m-2 p-5 border border-[#1D4C75] rounded-lg">
      <div class="text-2xl font-bold">{{ $ctx.locale == 'en' ? selectedSection.enName : selectedSection.arName }}</div>
      <template v-if="selectedSection.key == 'homescreen'">
        <HomeScreenSettings />
      </template>
      <template v-else-if="selectedSection.key == 'hwid'">
        <div class="input-label flex gap-2 w-1/2 items-center">
          <label for="input" class="text-base w-1/2">{{ t('hwid') }}</label>
          <el-input v-model="hwid"/>
          <el-button @click="setMachineGUID">{{ t('update') }}</el-button>
        </div>
        <div class="text-small">
          {{ t('currentMachine') }}: {{
            $ctx.locale === 'ar' ? $ctx.currentMachine.arName : $ctx.currentMachine.enName
          }}
        </div>
      </template>
      <template v-else-if="selectedSection.isPrivate && hasGroupedSettings(selectedSection)">
        <template v-for="(group, gi) in getGroupedSettings(selectedSection)" :key="gi">
          <div v-if="gi > 0" class="my-3 border-t border-dashed border-gray-300"></div>
          <div v-for="(setting, j) in group.settings" :key="setting.id">
            <SettingInput :setting="setting" :force-ltr="setting.key == 'company_phone1' || setting.key == 'company_phone2'"></SettingInput>
          </div>
        </template>
      </template>
      <template v-else>
        <div v-for="(setting, j) in selectedSection.settings" :key="j">
          <SettingInput :setting="setting" :force-ltr="setting.key == 'company_phone1' || setting.key == 'company_phone2'"></SettingInput>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import Location from '@/models/company/Location'
import {useAuth} from '@/plugins/auth'
import SettingInput from "@/components/settings/SettingInput.vue";
import {Icon} from '@iconify/vue';
import {SettingSectionInterface, useSettingsStore} from "@/stores/settingsStore";
import {ElMessage, ElMessageBox} from "element-plus";
import cheatbeep from "@/assets/sounds/cheat.mp3";
import {useHTTP} from "@/plugins/http";
import BackupUpload from './BackupUpload.vue';
import HomeScreenSettings from './HomeScreenSettings.vue';

export default defineComponent({
  components: {
    SettingInput,
    Icon,
    BackupUpload,
    HomeScreenSettings
  },
  setup() {
    const selectedPrinter = ref('')
    const selectedReceiptSize = ref('')
    const selectedAccountStatementSize = ref('')
    const selectedTransactionExpenseSize = ref('')

    const {locale, t} = useI18n({
      messages: {
        en: {
          confirmMsg: 'Are you sure? This will reload the app and close all open tabs',
          hwid: 'Hardware ID',
          update: "Update",
          currentMachine: "Current Machine",
          salesRepRequired: 'Sales Rep field Required',
          applySettingsToAll: 'Apply Settings To All',
          applySettingsToAllConfirm: 'Are you sure you want to apply settings to all machines?',
          applySettingsToAllSuccess: 'Settings successfully applied to all machines',
          applySettingsToAllError: 'Failed to apply settings to all machines'
        },

        ar: {
          confirmMsg: 'يرجى التأكيد. ستقوم هذه العملية بغلق جميع النوافذ واعادة تحميل النظام',
          hwid: 'معرف الجهاز',
          update: "تحديث",
          currentMachine: "الجهاز الحالي",
          salesRepRequired: 'حقل ممثل البيع اجباري',
          applySettingsToAll: 'تطبيق الإعدادات على جميع الأجهزة',
          applySettingsToAllConfirm: 'هل أنت متأكد من تطبيق الإعدادات على جميع الأجهزة؟',
          applySettingsToAllSuccess: 'تم تطبيق الإعدادات على جميع الأجهزة بنجاح',
          applySettingsToAllError: 'فشل في تطبيق الإعدادات على جميع الأجهزة'
        }
      }
    })

    const currentLocale = ref(locale.value)
    const locations = [
      {
        id: '7929d71b-833f-447b-8042-d3cf137612e9',
        name: 'Al-Mansour Store',
        type: 'store'
      },
      {
        id: '87f44a21-0b6d-4150-b82f-af458d2c2db1',
        name: 'Al-Zayona Store',
        type: 'store'
      },
      {
        id: 'fe462e64-d8ba-43ef-9507-99ec3c2da5e4',
        name: 'Karada Warehouse',
        type: 'warehouse'
      }
    ].map((loc) => Location.from(loc))

    return {
      t,
      currentLocale,
      locations,
      selectedPrinter,
      selectedReceiptSize,
      selectedAccountStatementSize,
      selectedTransactionExpenseSize,
      useSettingsStore
    }
  },

  async beforeMount() {
    // if (isElectron()) {
    //   try {
    //     const { ipcRenderer } = await import('electron')
    //     ipcRenderer.on('printers-list', (_e, args) => {
    //       this.printers = args
    //     })
    //     ipcRenderer.send('get-printers')
    //   } catch (error) {
    //     // do nothing
    //   }
    // }

    this.selectedReceiptSize = this.$ctx.getReceiptSize()
    this.selectedAccountStatementSize = this.$ctx.getAccountStatementSize()
    this.selectedTransactionExpenseSize = this.$ctx.getTransactionExpenseSize()


    let filteredSettings = [...useSettingsStore().sections];

    // Filter out Examination section if examinations_module is not true
    const examinationsModuleEnabled = useSettingsStore().getValue('examinations_module') === 'true';
    if (!examinationsModuleEnabled) {
      filteredSettings = filteredSettings.filter(section => section.enName !== 'Examination');
    }

    // Filter out Queue section if show_queue_button_on_customers_page is not true
    const queueButtonEnabled = useSettingsStore().getValue('show_queue_button_on_customers_page') === 'true';
    if (!queueButtonEnabled) {
      filteredSettings = filteredSettings.filter(section => section.enName !== 'Queue Screen');
    }

    const glassesButtonEnabled = useSettingsStore().getValue('show_glasses_screen_button_on_customers_page') === 'true';
    if (!glassesButtonEnabled) {
      filteredSettings = filteredSettings.filter(section => section.enName !== 'Glasses Screen');
    }

    // Filter out Vet section if vet_module is not true
    const vetModuleEnabled = useSettingsStore().getValue('vet_module') === 'true';
    if (!vetModuleEnabled) {
      filteredSettings = filteredSettings.filter(section => section.enName !== 'Vet');
    }
    this.settings = filteredSettings;

    if (this.settings.length) {
      this.selectedSection = this.settings[0];
    }
  },

  data() {
    const tabsPosition = this.$ctx.locale === 'en' ? 'left' : 'right'
    return {
      tabsPosition,
      currentLocation: this.$ctx.currentLocation,
      currentPrinter: this.$ctx.currentPrinter,
      printers: [],
      USDtoIQDRate: this.$ctx.exchangeRates.IQD,
      IQDtoUSDRate: this.$ctx.exchangeRates.USD,
      receiptLanguage: this.$ctx.receiptLanguage,
      machineTimezone: this.$ctx.machineTimezone,
      isSalesRepRequired: this.$ctx.isSalesRepRequired,

      printSizes: [
        {label: 'Large Print (A4 Paper)', value: 'a4'},
        {label: 'Small Print (Receipt Paper)', value: 'receipt'}
      ],
      hwid: this.$ctx.machineGUID,
      settings: [] as Array<SettingSectionInterface>,
      selectedSection: null as any,
      keySequence: [],
      expectedSequence: ['Escape', 's', 'a', 'l', 'o', 'm', 'Enter'],
    }
  },

  mounted() {
    window.addEventListener('keyup', this.handleEscKey);
  },
  unmounted() {
    useSettingsStore().showPrivateSettings = false;
    window.removeEventListener('keyup', this.handleEscKey);
  },

  watch: {
    currentLocale() {
      if (confirm(this.t('confirmMsg'))) {
        this.$ctx.setLocale(this.currentLocale)
      }
    },

    currentLocation() {
      this.$ctx.setLocation(this.currentLocation as Location)
    },

    currentPrinter() {
      this.$ctx.setPrinter(this.currentPrinter)
    },

    USDtoIQDRate() {
      this.$ctx.setExchangeRates({
        IQD: this.USDtoIQDRate,
        USD: this.IQDtoUSDRate
      })
    },

    IQDtoUSDRate() {
      this.$ctx.setExchangeRates({
        IQD: this.USDtoIQDRate,
        USD: this.IQDtoUSDRate
      })
    }
  },

  methods: {
    async setMachineGUID() {
      if (confirm('This operation will log you out and refresh the system, are you sure?')) {
        this.$ctx.setMachineGUID(this.hwid)
        const { userLogout } = useAuth()
        await userLogout()
        this.$router.replace('/login')
      }
    },
    changeSection(section: any) {
      this.selectedSection = null
      this.$nextTick(() => {
        this.selectedSection = section
      })
    },
    handleEscKey(event: any) {
      const key = event.key;

      // Add the key to the sequence array
      this.keySequence.push(key);

      // Check if the sequence length exceeds the expected sequence length
      if (this.keySequence.length > this.expectedSequence.length) {
        this.keySequence.shift(); // Remove the oldest key if sequence is too long
      }

      // Compare the current sequence with the expected sequence
      if (this.keySequence.join('') === this.expectedSequence.join('')) {
        const cheatAudio = new Audio(cheatbeep);
        cheatAudio.play();
        console.log('cheat activated!');
        ElMessage.success({
          dangerouslyUseHTMLString: true,
          message: `<h2>cheat activated!</h2>`
        })
        useSettingsStore().showPrivateSettings = true; // Call your desired action
        this.keySequence = []; // Reset the sequence
      }
    },
    hasGroupedSettings(section: any) {
      return section.settings.some((s: any) => s.settingGroup)
    },
    getGroupedSettings(section: any) {
      const groups: Array<{ name: string; settings: any[] }> = []
      const groupMap: Record<string, any[]> = {}

      for (const setting of section.settings) {
        const groupName = setting.settingGroup || '_ungrouped'
        if (!groupMap[groupName]) {
          groupMap[groupName] = []
          groups.push({ name: groupName, settings: groupMap[groupName] })
        }
        groupMap[groupName].push(setting)
      }

      return groups
    },
    async applySettingsToAll() {
      ElMessageBox.confirm(this.t('applySettingsToAllConfirm'), 'Confirm', {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning',
      }).then(async () => {
        try {
          const http = useHTTP();
          await http.put('/settings/settings/apply-all');
          ElMessage.success(this.t('applySettingsToAllSuccess'));
        } catch (error: any) {
          ElMessage.error(error.title || this.t('applySettingsToAllError'));
        }
      })
    }
  }
})
</script>
